/**
 * The surface contract — layout and capability, never behaviour.
 *
 * Both inboxes share one component set. Two places genuinely diverge in the
 * prototype's own markup, and pretending otherwise produces a component with a
 * `surface` prop that rewrites its own grid:
 *
 *   |               | Personal                  | Unified                     |
 *   |---------------|---------------------------|-----------------------------|
 *   | Row grid      | 5 columns                 | 6 columns                   |
 *   | Column header | none                      | SENDER · RECEIVED IN · …    |
 *   | Hover actions | Archive Delete Unread Snooze | Reply Assign Archive Snooze |
 *   | Provider chip | on the subject            | on the mailbox-owner cell   |
 *   | Right cell    | attachment + date         | + status badge              |
 * *   | Open tracking | yes (CRM sends are tracked) | no                        |
 *
 * So the resolution is a shared `<MailRow>` that owns cell rendering, read
 * weight, selection, star and the hover strip — composed by two thin surface
 * components that own only the grid template, the column set and the action
 * set.
 *
 * THE RULE THAT KEEPS THAT FROM BECOMING A FORK: MailRow reads `columns` and
 * `actions` as DATA. It must never branch on `surface.kind`. If a change to
 * MailRow needs to know which inbox it is in, widen this contract instead —
 * the same discipline mailComposerModes.js already applies to composer modes.
 */


export const SURFACES = {
    personal: {
        kind: 'personal',
        gridTemplate: '20px 20px 20px 236px minmax(0,1fr) 88px',
        showColumnHeader: false,
        columnLabels: null,
        columns: ['select', 'star', 'important', 'sender', 'subject', 'meta'],
        // `reply` is a DELIBERATE divergence from the prototype, which draws
        // four actions here. The live personal list has always carried an
        // always-visible Reply, and removing a working control is a capability
        // loss rather than a design refinement — so the redesign keeps it.
        rowActions: ['reply', 'archive', 'delete', 'markUnread', 'snooze'],
        providerChipSlot: 'subject',
        showStatusBadge: false,
        // Assignment is meaningless where there is exactly one reader.
        supportsAssign: false,
        // CRM-originated sends carry open/click tracking; provider mail does
        // not, and the unified inbox has no CRM sends in it at all.
        showTrackingIcons: true,
    },
    unified: {
        kind: 'unified',
        gridTemplate: '20px 20px 20px 200px 248px minmax(0,1fr) 196px',
        showColumnHeader: true,
        columnLabels: ['', '', '', 'Sender', 'Received in', 'Subject', 'Status'],
        columns: ['select', 'star', 'important', 'sender', 'owner', 'subject', 'meta'],
        // `delete` here means "remove from this inbox", which is a MANAGER
        // verb — the surface filters it out below that grant.
        // `markUnread` belongs here as much as it does on the personal lane.
        // It was absent from the row strip while PRESENT in `bulkActions`
        // below, so the action existed for a selection and not for a single
        // row — an asymmetry with no reason behind it (#693).
        rowActions: ['reply', 'assign', 'archive', 'delete', 'markUnread', 'snooze'],
        providerChipSlot: 'owner',
        showStatusBadge: true,
        supportsAssign: true,
        showTrackingIcons: false,
    },
};

/**
 * Drafts offer exactly one verb, and it is not the surface's.
 *
 * Everything else on either strip is a statement about a MESSAGE — Reply,
 * Archive, Snooze, Mark unread, Assign, Star — and a draft has been sent to
 * nobody, sits in no thread and carries no provider state to file. Until this
 * existed, `drafts` fell through to the `inbox` vocabulary and the view offered
 * five or six verbs of which every single one was a no-op against a row that is
 * not in `communications` at all. That is the same rule `bulkActionsFor`
 * already states for Trash: a view must not advertise a verb that is
 * meaningless in it.
 *
 * **Not gated by `canDelete`.** That is the unified lane's manager floor for
 * removing mail from a SHARED view, and a draft is not shared — `owner_filter`
 * in `services/mail_drafts.py` scopes the store to its author, so the only
 * person who can see this row is the one who wrote it. Requiring a manager
 * grant to throw away your own half-written message would be a floor with
 * nothing underneath it.
 */
const DRAFT_ACTIONS = Object.freeze(['delete']);

/**
 * The row's action strip for one VIEW, narrowed by what the viewer may do.
 *
 * Trash is the one view whose verbs are not the surface's: a message in the
 * bin can leave it in exactly two directions, and offering Archive, Snooze or
 * Mark unread on a row that is already deleted is offering to file something
 * in two places at once. Both tables derive the strip here so the two lanes
 * cannot disagree about what Trash offers — the unified one filtered `delete`
 * inline and would otherwise have grown a second, divergent rule.
 *
 * Drafts are the second such view; see `DRAFT_ACTIONS`.
 *
 * `canDelete` is the unified lane's manager floor. It gates Restore as well:
 * putting a message back into a shared view is the same curation as taking it
 * out, so it cannot be the weaker permission.
 */
export function rowActionsFor(surface, { folder = 'inbox', canDelete = true } = {}) {
    if (folder === 'drafts') return DRAFT_ACTIONS;
    if (folder === 'trash') {
        return canDelete ? ['restore', 'delete'] : [];
    }
    return canDelete
        ? surface.rowActions
        : surface.rowActions.filter((action) => action !== 'delete');
}

/**
 * The folder part of `bulkActionsFor` — what each view offers BEFORE the read
 * direction is resolved and `delete` is permission-filtered.
 *
 * `READ` and `ARCHIVE` are placeholders, not keys: the resolver swaps in
 * `markRead`/`markUnread` and `archive`/`unarchive` from the selection
 * summary. The folder vocabulary mirrors
 * `services/mail_folders.py::folder_clauses`, so a view the rail can show is a
 * view this table can describe.
 *
 * **`ARCHIVE` is resolved by the SELECTION in every folder, including the ones
 * where the answer looks fixed.** It could have been hard-coded for `inbox`
 * (nothing archived is in the Inbox) and `archived` (everything there is), and
 * it was — which is precisely how `all` came to be broken: it had no entry,
 * fell back to the Inbox template, and offered *Archive* on rows whose own
 * hover strip said *Move to Inbox* (#710). A per-folder answer is a rule that
 * has to be restated correctly for every folder anyone adds; reading the
 * selection is a rule that cannot be forgotten. In the fixed folders it
 * resolves to the same verb.
 *
 * `unsnooze` and `unmarkImportant` stay per-folder: those views are defined BY
 * that state, so there is nothing for a selection to disagree about.
 */
const ACTIONS_BY_FOLDER = Object.freeze({
    inbox: ['ARCHIVE', 'delete', 'READ', 'snooze', 'markImportant', 'tag'],
    archived: ['ARCHIVE', 'delete', 'READ', 'snooze', 'markImportant', 'tag'],
    snoozed: ['ARCHIVE', 'delete', 'READ', 'unsnooze', 'markImportant', 'tag'],
    important: ['ARCHIVE', 'delete', 'READ', 'snooze', 'unmarkImportant', 'tag'],
    all: ['ARCHIVE', 'delete', 'READ', 'snooze', 'markImportant', 'tag'],
});

/**
 * The bulk bar's actions for one VIEW, narrowed by folder, selection and what
 * the viewer may do (#710).
 *
 * A bulk action is a STATEMENT about a selection, so it must not offer a verb
 * that is meaningless or one-way in the view it appears in: Snooze on a row
 * already snoozed, Archive inside Archived, or a Mark unread over a selection
 * that is entirely unread — every one fired a write that hit its own
 * transition guard in `services/mail_state.py` and changed nothing while the
 * toast claimed success. The row strip already derives its verbs per view
 * (`rowActionsFor`); this is the same discipline for the selection above it,
 * in one place so the two lanes cannot disagree.
 *
 * `selection` is a `useMailSelection` return value (or the subset below), and
 * two of its summaries decide directions:
 *
 *   - `hasUnread`   → any unread selected means **Mark read**; all read means
 *                     **Mark unread**.
 *   - `hasArchived` → any archived selected means **Move to Inbox**; none
 *                     archived means **Archive**. One button, never both.
 *
 * `delete` is filtered when `canDelete` is false in EVERY folder — removing
 * mail is a manager verb wherever it appears.
 */
export function bulkActionsFor(
    surface,
    { folder = 'inbox', canSelect = true, canDelete = true, selection = {} } = {},
) {
    if (!canSelect || !['personal', 'unified'].includes(surface?.kind)) return [];

    // Drafts: one verb. Everything else on either strip is a statement about a
    // message that has been sent, threaded or filed.
    if (folder === 'drafts') return [...DRAFT_ACTIONS];

    // Trash swaps the strip for the two doors out of the bin — the row strip's
    // rule, and the selection's too. A viewer (or a responder on the unified
    // lane) has no curation standing there.
    if (folder === 'trash') {
        return canDelete ? ['restore', 'delete'] : [];
    }

    // `all` is now a first-class entry; the fallback is it rather than `inbox`,
    // so a folder nobody has listed yet gets the selection-resolved verbs
    // instead of the Inbox's fixed ones. That fallback IS what broke `all`.
    const template = ACTIONS_BY_FOLDER[folder] || ACTIONS_BY_FOLDER.all;
    const readKey = selection.hasUnread ? 'markRead' : 'markUnread';
    const archiveKey = selection.hasArchived ? 'unarchive' : 'archive';
    return template
        .map((key) => {
            if (key === 'READ') return readKey;
            if (key === 'ARCHIVE') return archiveKey;
            return key;
        })
        // #646 — `tag` needs no flag of its own: the unified lane's selection
        // floor (`canSelect`) is already responder, the grant tagging needs.
        .filter((key) => key !== 'delete' || canDelete);
}
